import Hero from "@/components/Hero";
import VacancyList from "@/components/Vacancy/VacancyPage/VacancyList";
import { VacancyDetail } from "@/components/Vacancy/VacancyDetail/VacancyDetail";
import JobApply from "@/components/JobApply";
import ConfirmationPopup from "@/components/Popup";
import { IVacancyDetail, PopupTypes, Vacancy } from "@/services/interfaces/VacancyInterface";
import router from "next/router";
import { useSession } from "next-auth/react";
import { useState } from "react";
import { Title } from "../Title";
import { useResendConfirmEmail } from "@/services/AuthenticationService";
import encryptData from "@/functions/encryptInput";
import { Button } from "antd";
import Modal from "../Modal";


const VacancyLayout = ({
    isDetailPage,
    data,
    popupState,
    setPopup,
    isApplied
}) => {

    const { data: session } = useSession();
    const { resendConfirmEmail } = useResendConfirmEmail();

    const isEmailVerified = session ? session["email_verified"] : false;
    const [selectedItem, setSelectedItem] = useState<Vacancy | IVacancyDetail | null>(null);
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [showSendEmailSuccess, setShowEmailSuccess] = useState<boolean>(false);

    const convertDate = (dataDate) => {
        const date = new Date(dataDate);
        const day = date.getUTCDate();
        const month = date.toLocaleString("default", { month: "long" });
        const year = date.getUTCFullYear();

        return `${day} ${month} ${year}`;
    };

    const handleApplyButtonClick = (item) => {
        if (!session?.user) {
            router.push("/login");
            return;
        }

        const currentDate = new Date();
        const endDate = item.endDate ? new Date(item.endDate) : new Date(item.deadline);
        if (currentDate > endDate || !isEmailVerified) {
            setPopup(PopupTypes.ShowErrorPopup, true);
        } else {
            setSelectedItem(item);
            setPopup(PopupTypes.ShowJobApply, true);
        }
    };

    const handleCopyLink = (id) => {
        const linkToCopy = `${window.location.origin}/vacancy/detail/${id}`;
        // Check if the environment is secure (HTTPS) and clipboard API is available
        if (navigator.clipboard && window.isSecureContext) {
            navigator.clipboard.writeText(linkToCopy)
                .then(() => {
                    setSuccessMessage("Vacancy link has been copied successfully");
                    setPopup(PopupTypes.ShowSuccessPopup, true);
                })
                .catch((err) => {
                    console.error("Failed to copy link using clipboard API:", err);
                });
        } else {
            const tempInput = document.createElement("input");
            tempInput.value = linkToCopy;
            document.body.appendChild(tempInput);
            tempInput.select();
            document.execCommand("copy");
            document.body.removeChild(tempInput);
            setPopup(PopupTypes.ShowSuccessPopup, true);
            console.log("Link copied using execCommand fallback");
        }
    };

    const handleResendEmail = async () => {
        setPopup(PopupTypes.ShowErrorPopup, false);
        const email = session?.user?.email ?? "";
        if (email) {
            const encryptedEmail = await encryptData(email);
            await resendConfirmEmail(encryptedEmail);
        }
        setShowEmailSuccess(true);
    }

    return (
        <div>
            {isDetailPage ? (
                <>
                    <Title>{data?.title || ""}</Title>
                    <Hero backgroundImage="/hero-bg.png">
                        <h2 className="text-lg font-semibold text-white pt-3">
                            <a className="hover:text-white" onClick={() => router.push("/vacancy")}>
                                Vacancy
                            </a>{" "}
                            / <span className="text-primary">Detail</span>
                        </h2>
                        <h2 className="text-3xl font-bold text-white pt-3">{data?.title}</h2>
                        <h2 className="text-lg font-normal text-white pt-2">
                            {data?.city}, Indonesia
                        </h2>
                        <div className="flex space-x-8 text-white pt-4">
                            <div className="flex gap-3 items-center">
                                <img src="/business-unit-icon.svg" alt="" className="h-6" />
                                <p className="text-xs">{data?.company}</p>
                            </div>
                            <div className="flex gap-3 items-center">
                                <img src="/apply-deadline-clock.svg" alt="" className="h-6" />
                                <p className="text-xs">
                                    Apply Deadline : {convertDate(data?.deadline)}
                                </p>
                            </div>
                            <div className="flex gap-1 items-center">
                                <img src="/experience-level-icon.svg" alt="" className="h-2" />
                                <p className="text-xs pr-6">{data?.category}</p>
                            </div>
                        </div>
                        <div className="pb-5 border-b-2">
                        <button
                            onClick={() => handleApplyButtonClick(data as IVacancyDetail)}
                            className={`text-xs ${isApplied ? "bg-white text-primary border border-primary" : "bg-primary text-white hover:bg-primary-dark"} font-semibold px-10 py-2 mt-4`}
                            disabled={isApplied}
                        >
                            {isApplied == false ? 'Apply' : 'Applied'}
                        </button>
                        </div>
                    </Hero>
                </>
            ) : (
                <>
                    <Title>Vacancy</Title>
                    <Hero backgroundImage="hero-bg.png">
                        <div className="p-6 text-white md:p-0">
                            <h1 className="text-2xl">Jobs</h1>
                            <h2 className="pt-3">
                                We offer fair opportunities to individuals of all backgrounds, whether
                                they&apos;re young up-and-comers or seasoned professionals. <br />{" "}
                                Choose the category that aligns with your level of experience!
                            </h2>
                        </div>
                    </Hero>
                </>
            )}

            <div>
                {isDetailPage ? (
                    <VacancyDetail data={data} />
                ) : (
                    <VacancyList
                        data={data}
                        convertDate={convertDate}
                        handleCopyLink={handleCopyLink}
                        handleApplyButtonClick={handleApplyButtonClick}
                    />
                )}
            </div>

            {popupState[PopupTypes.ShowJobApply] && (
                <JobApply selectedItem={selectedItem} onClose={() => setPopup(PopupTypes.ShowJobApply, false)} showCancel={true} />
            )}
            {popupState[PopupTypes.ShowSuccessPopup] && (
                <ConfirmationPopup
                    message={successMessage}
                    onConfirm={() => setPopup(PopupTypes.ShowSuccessPopup, false)}
                    confirmLabel="Ok"
                    confirmBtnStyle={2}
                    popupSize={1}
                />
            )}
            
            {popupState[PopupTypes.ShowErrorPopup] && (
                isEmailVerified 
                ? <ConfirmationPopup
                    header="Your application did not go through"
                    message="Deadline for applying has already passed"
                    imageSrc="\circum_calendar.png"
                    popupSize={1}
                    withRemovePopupBtn={() => setPopup(PopupTypes.ShowErrorPopup, false)}
                /> 
                : <ConfirmationPopup
                    header="Your application did not go through"
                    message="Please verify your email first. If you didn't receive verification email in your inbox, please check your spam email"
                    imageSrc="\failed-apply-vacancy.svg"
                    popupSize={1}
                    onConfirm={handleResendEmail}
                    confirmLabel="Resend Verification Email"
                    confirmBtnStyle={2}
                    onCancel={() => setPopup(PopupTypes.ShowErrorPopup, false)}
                    cancelLabel="Back to vacancy"
                    cancelBtnStyle={4}
                /> 
            )}

            <Modal
                open={showSendEmailSuccess}
                modalTitle="Success"
                imageUri="change-password-popup.jpg"
            >
                <p className="mt-4 font-medium text-slate-500 text-base text-center max-w-sm">
                    Verification email is sent successfully. Please check your email.
                </p>
                <Button 
                    onClick={() => setShowEmailSuccess(false)}
                    className="w-full my-8 h-fit py-2 text-center text-white bg-[#93885B] rounded-xl">
                    Ok
                </Button>
            </Modal>
        </div>
    );
};

export default VacancyLayout;
